import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tasksService } from '../api/tasks.service';
import { adaptTaskSummaryDto } from '../adapters';
import { serializeFilters, serializeSort } from '../utils/filter-serializer';
import { TASK_KEYS } from './queryKeys';
import { TASK_COLLECTION_STALE_TIME } from './taskQueryConfig';
import { seedTaskEntities } from './useTaskEntityCache';
import type {
  GroupByOption,
  Task,
  TaskApiParams,
  TaskFilters,
  TaskGroupResult,
  TaskListSortBy,
} from '../types';

const PAGE_SIZE = 20;

interface UseTaskGroupInfiniteParams {
  projectId: string;
  boardId?: string;
  groupBy: GroupByOption;
  groupKey: string;
  sortBy?: TaskListSortBy;
  filters: TaskFilters;
  enabled?: boolean;
}

export interface TaskGroupPage {
  key: string;
  label: string;
  color: string | null;
  count: number;
  items: Task[];
  nextCursor: string | null;
}

/**
 * Fetches tasks for a single Kanban column using cursor pagination.
 *
 * @param params.groupKey - The column identifier (status ID, assignee ID, etc.) whose tasks to load.
 * @param params.enabled - Set to `false` to defer fetching until the column is visible.
 * @returns Infinite query result; `data.pages` each contain the column metadata plus one page of tasks.
 *
 * @remarks
 * The API always returns exactly one group when `groupKey` is specified, so only `groups[0]` is
 * consumed from the response. If the server returns an empty `groups` array (unexpected), the hook
 * returns an empty page rather than throwing, to avoid breaking the entire board.
 *
 * Disabled automatically when `activeOrganizationId` is not set (org switching in progress).
 */
export function useTaskGroupInfinite({
  projectId,
  boardId,
  groupBy,
  groupKey,
  sortBy,
  filters,
  enabled = true,
}: UseTaskGroupInfiniteParams) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);
  const queryClient = useQueryClient();

  const serializedFilters = serializeFilters(filters);
  const serializedSort = sortBy ? serializeSort(sortBy) : undefined;

  const baseParams: TaskApiParams = {
    limit: PAGE_SIZE,
    view: 'kanban',
    groupBy,
    groupKey,
    ...(boardId && { boardId }),
    ...(serializedFilters && { filters: serializedFilters }),
    ...(serializedSort && { sort: serializedSort }),
    ...(filters.search && { search: filters.search }),
  };

  return useInfiniteQuery<TaskGroupPage>({
    queryKey: TASK_KEYS.groupPage(
      orgId ?? '',
      projectId,
      groupBy,
      groupKey,
      boardId,
      serializedFilters,
      filters.search,
      serializedSort,
    ),
    queryFn: async ({ pageParam, signal }): Promise<TaskGroupPage> => {
      const result = await tasksService.getQuery(
        projectId,
        {
          ...baseParams,
          cursor: pageParam as string | undefined,
        },
        signal,
      );
      const group: TaskGroupResult | undefined = result.groups[0];
      if (!group) {
        return {
          key: groupKey,
          label: groupKey,
          color: null,
          count: 0,
          items: [],
          nextCursor: null,
        };
      }
      const tasks = group.items.map(adaptTaskSummaryDto);
      seedTaskEntities(queryClient, orgId ?? '', tasks);
      return {
        key: group.key,
        label: group.label,
        color: group.color,
        count: group.count,
        items: tasks,
        nextCursor: group.nextCursor,
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: TASK_COLLECTION_STALE_TIME,
    enabled: !!orgId && !!projectId && enabled,
  });
}
